/**
 * DataCardsSection — fetches records from a bound table and renders them as cards.
 * Supports: section title/description, card styling, Show More button.
 */

import React from 'react';
import { usePublicTableList } from '../../../hooks/usePublicTableView';
import DataSectionWrapper from '../DataSectionWrapper';

export default function DataCardsSection({ config, dataBinding, theme }) {
  const cfg         = config || {};
  const tableSlug   = dataBinding?.table_slug;
  const fieldMap    = dataBinding?.field_mapping || {};
  const limit       = dataBinding?.limit || 6;
  const sort        = dataBinding?.sort;
  const columns     = cfg.columns || 3;
  const imageHeight = cfg.image_height ?? 180;
  const cardStyle   = cfg.card_style || 'elevated';
  const cardBg      = cfg.card_bg_color || '#ffffff';
  const accentColor = cfg.accent_color || (theme?.primary || 'var(--primary-color)');
  const hoverLift   = cfg.hover_lift !== false;

  const params = {
    page_size: limit,
    ...(sort?.field && { sort: sort.field, sort_dir: sort.direction || 'desc' }),
  };

  const { data, isLoading, isError } = usePublicTableList(tableSlug, params);
  const records = data?.records || data?.results || [];

  // Field mapping: prefer section-level mapping, fall back to view_config mapping
  const viewMapping = data?.view_config?.field_mapping || {};
  const { title: titleField, description: descField, image: imageField, badge: badgeField } = {
    ...viewMapping,
    ...fieldMap,
  };

  const colClass = columns === 2 ? 'col-md-6'
    : columns === 4 ? 'col-md-3'
    : 'col-md-4';

  const cardBoxShadow =
    cardStyle === 'elevated' ? '0 2px 12px rgba(0,0,0,0.09)'
    : cardStyle === 'glass'  ? '0 4px 24px rgba(0,0,0,0.08)'
    : 'none';
  const cardBorder = cardStyle === 'outlined' ? '1px solid #e2e8f0' : '1px solid transparent';
  const cardBackdrop = cardStyle === 'glass' ? 'blur(8px)' : undefined;

  if (!tableSlug) return null;

  if (isLoading) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="row g-3">
          {[...Array(columns)].map((_, i) => (
            <div key={i} className={`col-6 ${colClass}`}>
              <div style={{ background: '#f1f5f9', borderRadius: 12, overflow: 'hidden' }}>
                {imageHeight > 0 && <div style={{ height: imageHeight, background: '#e2e8f0' }} />}
                <div className="p-3">
                  <div style={{ height: 14, background: '#e2e8f0', borderRadius: 4, marginBottom: 8, width: '70%' }} />
                  <div style={{ height: 12, background: '#e2e8f0', borderRadius: 4 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </DataSectionWrapper>
    );
  }

  if (isError || records.length === 0) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-center text-muted py-4">
          {isError ? 'Failed to load data.' : 'No records found.'}
        </div>
      </DataSectionWrapper>
    );
  }

  return (
    <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
      <div className="row g-3">
        {records.map((record, idx) => {
          const title = titleField ? record[titleField] : Object.values(record)[0];
          const desc  = descField  ? record[descField]  : null;
          const img   = imageField ? record[imageField] : null;
          const badge = badgeField ? record[badgeField] : null;

          return (
            <div key={idx} className={`col-6 ${colClass}`}>
              <div
                className="h-100 rounded overflow-hidden"
                style={{
                  background: cardBg,
                  border: cardBorder,
                  boxShadow: cardBoxShadow,
                  backdropFilter: cardBackdrop,
                  borderRadius: 'var(--card-radius, 12px)',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => {
                  if (hoverLift) {
                    e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = cardBoxShadow;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {img && imageHeight > 0 && (
                  <img src={img} alt={String(title || '')} style={{ width: '100%', height: imageHeight, objectFit: 'cover' }} />
                )}
                <div className="p-3">
                  {badge && (
                    <span className="badge mb-2" style={{ background: accentColor, color: '#fff', borderRadius: 100 }}>
                      {badge}
                    </span>
                  )}
                  <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{String(title || '')}</div>
                  {desc && (
                    <div className="text-muted mt-1" style={{ fontSize: '0.85rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {String(desc)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DataSectionWrapper>
  );
}
